import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Formik,
  Field,
  FormikProps,
  FieldArray,
  FieldArrayRenderProps,
} from "formik";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useToast } from "../../../components/Toast";
import {
  getEmptyProjectExpenseEntry,
  mapProjectExpenseEntryToApiRequest,
  ProjectExpenseEntry,
  ProjectExpenseFormData,
} from "../components/projectExpense/types";
import { projectExpenseApi } from "../services/projectExpenseApiService";
import { ProjectExpenseForm } from "../components/projectExpense";
import { Loading } from "../../../components/Loading";
import { Autocomplete, CircularProgress, Grid } from "@mui/material";
import { Vehicle } from "../../../services/api";
import NavigationHeader from "../../../components/NavigationHeader";
import { ProjectTabEnum, PROJECT_TERMINOLOGY } from "../enums";
import { projectMasterDataApi } from "../services/projectMasterDataApiService";
import { uploadFile } from "../../../utils/UploadFile";
import { vehicleExpenseApi } from "../../../pages/vehicleExpense/apiService";
import {
  displayErrorToast,
  ExpenseEntry,
  ExpenseFormData,
  getEmptyExpenseEntry,
  mapExpenseEntryToApiRequest,
  hasOtherExpense,
} from "../../../pages/vehicleExpense/components/types";
import ExpenseForm from "../../../pages/vehicleExpense/components/ExpenseForm";
import { v4 as uuidv4 } from "uuid";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import { ProjectDetailTabs } from "../types";
import { validationSchema } from "../../../pages/vehicleExpense/validationSchema";
import GenericOtherExpenseFields from "../../../pages/vehicleExpense/components/GenericOtherExpenseFields";

const EXPENSE_TYPES = [
  "Fuel",
  "Maintenance / Repair",
  "Driver/Cleaner",
  "Parking",
  "Challan",
  "Other",
] as const;

const OUTSOURCED_EXPENSE_TYPES = [
  ...EXPENSE_TYPES,
  "Outsourced Owner",
] as const;

function ProjectAddExpense() {
  const { projectId, superProjectId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllExpenseTypes, setShowAllExpenseTypes] = useState(false);
  const { planStatus } = usePlanStatus();

  const initialValues: ExpenseFormData = {
    vehicleNumber: "",
    vehicleId: "",
    vehicle: undefined,
    expenses: [],
  };

  const handleSubmit = async (values: ExpenseFormData) => {
    try {
      setSubmitting(true);
      // Convert each expense entry to API format and create expenses
      const expenseRequests = values.expenses.map((expense) =>
        mapExpenseEntryToApiRequest(expense, "", values.vehicleId || "")
      );

      if (expenseRequests.length === 1) {
        // Single expense - use createExpense
        const result = await projectExpenseApi.createExpense(
          projectId!,
          expenseRequests[0]
        );
      } else if (expenseRequests.length > 1) {
        // Multiple expenses - use bulkCreateExpenses
        const results = await projectExpenseApi.bulkCreateExpenses(
          projectId!,
          expenseRequests
        );
      }

      const uploadPromises = values.expenses.flatMap(
        (expense) =>
          expense.selectedFiles?.map((file: File) =>
            uploadFile({
              file: file,
              documentType: "project-expense",
              documentTypeId: expense.id || "",
              documentContext: "project-expense",
              description: "Expense proof for project expense",
            })
          ) || []
      );
      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      showToast("Expenses saved successfully", "success");
      navigate(-1);
    } catch (error) {
      console.error("Error saving expenses:", error);
      showToast("Failed to save expenses", "error");
      setError("Failed to save expenses. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const loadVehicles = async () => {
      const vehicleData = await projectMasterDataApi.getProjectMasterDataByType(
        "VEHICLE",
        projectId!
      );
      setVehicles(vehicleData);
      setLoading(false);
    };
    loadVehicles();
  }, []);

  const renderExpenseTypeSelector = (
    arrayHelpers: FieldArrayRenderProps,
    expenses: ExpenseEntry[],
    acquisitionType: string
  ) => {
    const existingTypes = new Set(expenses.map((expense) => expense.type));
    const availableTypes =
      acquisitionType === "Outsourced"
        ? OUTSOURCED_EXPENSE_TYPES.filter((type) => !existingTypes.has(type))
        : EXPENSE_TYPES.filter((type) => !existingTypes.has(type));

    if (availableTypes.length === 0) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            borderRadius: 1,
            position: "sticky",
            top: 16,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.75rem" }}>
            All expense types added
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            Use the "Add Another" button in each section to add more expenses of
            the same type.
          </Typography>
        </Paper>
      );
    }

    // For outsourced, show priority types first with view more button
    if (acquisitionType === "Outsourced") {
      const priorityTypes = ["Outsourced Owner", "Driver/Cleaner", "Other"];
      const remainingTypes = availableTypes.filter(
        (type) => !priorityTypes.includes(type)
      );

      const showPriorityTypes = priorityTypes.filter(
        (type) => !existingTypes.has(type)
      );
      const showRemainingTypes = remainingTypes.filter(
        (type) => !existingTypes.has(type)
      );

      // If all priority types are added, show remaining types by default
      const shouldShowAllTypes = showPriorityTypes.length === 0;

      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            borderRadius: 1,
            position: "sticky",
            top: 16,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.75rem" }}>
            Select expense type to add:
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ gap: 1, mb: 1, alignItems: "center" }}
          >
            {showPriorityTypes.map((type) => (
              <Chip
                key={type}
                label={
                  type === "Maintenance / Repair"
                    ? "Maintenance / Repair"
                    : type
                }
                onClick={() =>
                  arrayHelpers.push(
                    getEmptyExpenseEntry(
                      type,
                      planStatus?.companyId,
                      planStatus?.user?.id,
                      null,
                      "VEHICLE_EXPENSE"
                    )
                  )
                }
                color="primary"
                variant="outlined"
                icon={<AddIcon sx={{ fontSize: "1rem" }} />}
                sx={{
                  width: "auto",
                  justifyContent: "flex-start",
                  height: 25,
                  borderRadius: 2,
                  "& .MuiChip-label": {
                    flex: 1,
                    textAlign: "left",
                    pl: 1,
                    fontSize: "0.75rem",
                  },
                }}
              />
            ))}

            {showRemainingTypes.length > 0 && (
              <>
                {!shouldShowAllTypes && !showAllExpenseTypes && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowAllExpenseTypes(true)}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      ml: "auto",
                    }}
                  >
                    View More
                  </Button>
                )}

                {(shouldShowAllTypes || showAllExpenseTypes) && (
                  <>
                    {showRemainingTypes.map((type) => (
                      <Chip
                        key={type}
                        label={
                          type === "Maintenance / Repair"
                            ? "Maintenance / Repair"
                            : type
                        }
                        onClick={() =>
                          arrayHelpers.push(
                            getEmptyExpenseEntry(
                              type,
                              planStatus?.companyId,
                              planStatus?.user?.id,
                              null,
                              "VEHICLE_EXPENSE"
                            )
                          )
                        }
                        color="primary"
                        variant="outlined"
                        icon={<AddIcon sx={{ fontSize: "1rem" }} />}
                        sx={{
                          width: "auto",
                          justifyContent: "flex-start",
                          height: 25,
                          borderRadius: 2,
                          "& .MuiChip-label": {
                            flex: 1,
                            textAlign: "left",
                            pl: 1,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </Stack>
        </Paper>
      );
    }

    return (
      <Paper
        elevation={3}
        sx={{
          p: 1.5,
          borderRadius: 1,
          position: "sticky",
          top: 16,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.75rem" }}>
          Select expense type to add:
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          {availableTypes.map((type) => (
            <Chip
              key={type}
              label={
                type === "Maintenance / Repair" ? "Maintenance / Repair" : type
              }
              onClick={() =>
                arrayHelpers.push(
                  getEmptyExpenseEntry(
                    type,
                    planStatus?.companyId,
                    planStatus?.user?.id,
                    projectId!,
                    "PROJECT_EXPENSE"
                  )
                )
              }
              color="primary"
              variant="outlined"
              icon={<AddIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                width: "auto",
                justifyContent: "flex-start",
                height: 25,
                borderRadius: 2,
                "& .MuiChip-label": {
                  flex: 1,
                  textAlign: "left",
                  pl: 1,
                  fontSize: "0.75rem",
                },
              }}
            />
          ))}
        </Stack>
      </Paper>
    );
  };

  const renderExpensesByType = (
    type: string,
    expenses: ExpenseEntry[],
    arrayHelpers: FieldArrayRenderProps,
    formikProps: FormikProps<ExpenseFormData>
  ) => {
    const typeExpenses = expenses.filter((expense) => expense.type === type);
    const startIndex = expenses.findIndex((expense) => expense.type === type);

    if (typeExpenses.length === 0) return null;

    return (
      <Box key={type} sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          color="primary"
          sx={{ mb: 1, fontSize: "0.875rem", fontWeight: 600 }}
        >
          {type === "Maintenance / Repair" ? "Maintenance / Repair" : type}{" "}
          Expenses
        </Typography>

        {typeExpenses.map((_, typeIndex) => {
          const index = startIndex + typeIndex;
          return (
            <ExpenseForm
              key={index}
              type={type}
              index={index}
              typeIndex={typeIndex}
              onDelete={() => {
                arrayHelpers.remove(index);
                const keyToDelete = expenses[index]?.id;
                if (formikProps.values.odometerReadings?.has(keyToDelete!)) {
                  const newMap = new Map(formikProps.values.odometerReadings);
                  newMap.delete(keyToDelete!);
                  formikProps.setFieldValue("odometerReadings", newMap);
                }
              }}
              formikProps={formikProps}
            />
          );
        })}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Button
            startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
            onClick={() =>
              arrayHelpers.push(
                getEmptyExpenseEntry(
                  type,
                  planStatus?.companyId,
                  planStatus?.user?.id,
                  projectId!,
                  "PROJECT_EXPENSE"
                )
              )
            }
            variant="outlined"
            size="small"
            sx={{ fontSize: "0.75rem", px: 2, py: 0.5 }}
          >
            Add Another {type} Expense
          </Button>
        </Box>
      </Box>
    );
  };

  // Get option label
  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return option.vehicleNumber;
  };

  // Render vehicle option
  const renderVehicleOption = (props: any, option: any) => (
    <Box component="li" {...props}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 500, fontSize: "0.75rem" }}
        >
          {option.vehicleNumber}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.55rem",
            color: "text.secondary",
            backgroundColor: "grey.100",
            px: 1,
            py: 0.25,
            borderRadius: 0.5,
          }}
        >
          {option?.acquisitionType}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Container sx={{ p: 1, my: 2 }}>
      <NavigationHeader
        title={`New ${ProjectTabEnum.EXPENSES.label}`}
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: superProjectId
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          ...(superProjectId
            ? [
                {
                  label: ProjectTabEnum.DIVISIONS.label,
                  path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                },
                {
                  label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                  path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                },
              ]
            : []),
          {
            label: ProjectTabEnum.EXPENSES.label,
            path: superProjectId
              ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.EXPENSE}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.EXPENSE}`,
          },
          {
            label: `New ${ProjectTabEnum.EXPENSES.label}`,
          },
        ]}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        // validateOnBlur={true}
      >
        {(formikProps: FormikProps<ExpenseFormData>) => {
          const hasVehicleSelected = Boolean(formikProps.values.vehicleId);
          return (
            <Box
              component="form"
              noValidate
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                formikProps.handleSubmit(e);
              }}
            >
              {loading ? (
                <Loading message="Loading vehicles..." fullScreen={true} />
              ) : (
                <Paper
                  elevation={3}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        freeSolo
                        options={vehicles}
                        loading={loading}
                        getOptionLabel={getOptionLabel}
                        value={formikProps.values.vehicleNumber}
                        onChange={(_, newValue, reason) => {
                          if (
                            reason === "selectOption" &&
                            typeof newValue === "object"
                          ) {
                            formikProps.setFieldValue(
                              "vehicleId",
                              newValue?.id || ""
                            );
                            formikProps.setFieldValue(
                              "vehicleNumber",
                              newValue?.vehicleNumber || ""
                            );
                            formikProps.setFieldValue("vehicle", newValue);
                            vehicleExpenseApi
                              .getCurrentOdometerReadings(
                                newValue?.id || "",
                                "PROJECT_EXPENSE"
                              )
                              .then((currentOdometerReadings) => {
                                const odometerReadingsMap = new Map<
                                  string,
                                  {
                                    index: number;
                                    odometerReading: number;
                                    fuelQuantity: number;
                                    readingDate: string;
                                  }
                                >();

                                currentOdometerReadings.forEach(
                                  (reading: any, index: number) => {
                                    const key = uuidv4();

                                    // Convert liters to number, default to 0 if null or invalid
                                    const fuelQuantity = reading.liters
                                      ? parseFloat(reading.liters)
                                      : 0;

                                    // Create the reading object with negative index to avoid conflicts with form entries
                                    const readingObj = {
                                      index: -(index + 1), // Use negative index for existing readings
                                      odometerReading: reading.odometerReading,
                                      fuelQuantity: fuelQuantity,
                                      readingDate: reading.expenseDate,
                                    };

                                    odometerReadingsMap.set(key, readingObj);
                                  }
                                );
                                // Set the map in formik values
                                formikProps.setFieldValue(
                                  "odometerReadings",
                                  odometerReadingsMap
                                );
                              });
                          } else if (reason === "clear") {
                            formikProps.setFieldValue("vehicleId", "");
                            formikProps.setFieldValue("vehicle", undefined);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Vehicle"
                            placeholder="Search vehicle number"
                            required
                            fullWidth
                            error={!!error}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={16}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            size="small"
                            InputLabelProps={{ sx: { fontSize: "0.75rem" } }}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "0.75rem" },
                            }}
                          />
                        )}
                        renderOption={renderVehicleOption}
                        noOptionsText={
                          loading
                            ? "Searching..."
                            : "No matching vehicle found. You can still type to add manually."
                        }
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      {hasVehicleSelected && (
                        <Box sx={{ ml: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontSize: "0.75rem", mb: 0.5 }}
                          >
                            Vehicle Fuel Type
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                          >
                            {formikProps.values.vehicle?.fuelType}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={6} md={6}>
                      <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem", mb: 0.5 }}
                        >
                          Total Amount
                        </Typography>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: 600, fontSize: "1rem" }}
                        >
                          ₹
                          {formikProps.values.expenses
                            .reduce(
                              (acc, expense) => acc + Number(expense.amount),
                              0
                            )
                            .toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}
              <FieldArray name="expenses">
                {(arrayHelpers: FieldArrayRenderProps) => (
                  <Grid container spacing={2}>
                    {hasVehicleSelected && (
                      <Grid
                        item
                        xs={12}
                        md={12}
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 100,
                        }}
                      >
                        {renderExpenseTypeSelector(
                          arrayHelpers,
                          formikProps.values.expenses,
                          formikProps.values.vehicle?.acquisitionType || ""
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12} md={12}>
                      {hasVehicleSelected ? (
                        <>
                          {(formikProps.values.vehicle?.acquisitionType ===
                          "Outsourced"
                            ? OUTSOURCED_EXPENSE_TYPES
                            : EXPENSE_TYPES
                          ).map((type) => {
                            return (
                              type !== "Other" &&
                              renderExpensesByType(
                                type,
                                formikProps.values.expenses,
                                arrayHelpers,
                                formikProps
                              )
                            );
                          })}
                          {hasOtherExpense(formikProps.values.expenses) && (
                            <GenericOtherExpenseFields
                              index={formikProps.values.expenses.length}
                              masterCategory="PROJECT_EXPENSE"
                              masterReferenceId={projectId}
                              companyId={planStatus?.companyId!}
                              createdBy={planStatus?.user?.id}
                              defaultDate={
                                new Date().toISOString().split("T")[0]
                              }
                            />
                          )}
                          {formikProps.values.expenses.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                                mt: 2,
                              }}
                            >
                              <Button
                                variant="outlined"
                                onClick={() => navigate(-1)}
                                disabled={submitting}
                                size="small"
                                sx={{ fontSize: "0.75rem", px: 2, py: 0.5 }}
                              >
                                Cancel
                              </Button>
                              <Button
                                // type="submit"
                                onClick={() => {
                                  displayErrorToast(
                                    formikProps.errors,
                                    showToast
                                  );
                                  formikProps.handleSubmit();
                                }}
                                variant="contained"
                                disabled={!formikProps.dirty || submitting}
                                size="small"
                                sx={{ fontSize: "0.75rem", px: 2, py: 0.5 }}
                              >
                                {submitting ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                    <span>Saving...</span>
                                  </Box>
                                ) : (
                                  "Save All Expenses"
                                )}
                              </Button>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            bgcolor: "action.disabledBackground",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem", mb: 0.5 }}
                          >
                            No Vehicle Selected
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            Please select a vehicle from the dropdown above to
                            start adding expenses.
                          </Typography>
                        </Paper>
                      )}
                    </Grid>
                  </Grid>
                )}
              </FieldArray>
            </Box>
          );
        }}
      </Formik>
    </Container>
  );
}

export default ProjectAddExpense;
